// Voice mode on ElevenLabs — 23 Sep 2026, Natanel: "we want to move to eleven labs voice model".
//
// THE SAME CONVERSATION AS realtime.ts, ANOTHER VENDOR. ElevenLabs' agent hears the recruiter and
// speaks to them — their voices, their turn-taking, Claude Haiku running kadmon's one-page script on
// their side — and every real request still reaches console-vera through `ask_vera`, answered here
// in the browser by the panel's own ask. The tools and the second yes a careful card needs are
// voice-tools.ts's, the same code OpenAI's conversation runs, so the rules cannot differ by vendor.
//
// HOW IT CONNECTS. `POST /voice/session` hands back a signed URL for one conversation with
// kadmon's agent (the brain API holds the key: services/bff/src/elevenlabs.ts), and ElevenLabs'
// own browser library opens the call on it over their WebSocket — the microphone out, her voice
// in. Until 24 Sep 2026 it was a conversation token over WebRTC, which took a second longer to
// open and lost the first words said (`ELEVENLABS_TRANSPORT` there has the measurement); the link
// below still opens either, whichever the session carries. The brain tier holds no socket and
// carries no audio, as with OpenAI. The recruiter's name is the one thing passed
// (`recruiter_name`, a variable the agent's script names); the agent allows no override of the
// script, the tools, the voice or the model.
//
// QUICKER TO OPEN (23 Sep 2026, Natanel: "we need it fast with the connection"). Until then a tap
// on Talk waited for three things in a row before the call even started to connect: the token
// (a quarter of a second from ElevenLabs, measured, and up to a second), ElevenLabs' browser
// library, fetched and parsed on that tap, and the look-up of their call server. So, as on OpenAI
// (realtime.ts), while the panel is open (`prepare`) the token is minted ahead and the library and
// the connection are warmed (`browserElevenLabsWarmUp`); Talk takes the token and connects at once.
// A token opens one conversation and lives fifteen minutes; one made ahead is used for ten. Since
// 24 Sep 2026 the token is a signed URL for their WebSocket (above) — the same fifteen minutes —
// and a mouse's press on Talk starts the call without waiting for the click (app/voice.tsx).
//
// THE PURE PART is `createElevenLabsVoice`, driven by elevenlabs.test.ts on a fake link;
// `browserElevenLabsLink` is the adapter onto `@elevenlabs/client`, loaded when the panel opens
// or at the latest on Talk, thin and untested like realtime.ts's WebRTC one.
import type { ElevenLabsVoiceSession } from '@vera/bff';
import type { OrbLevels } from './orb.ts';
import { startFillers, type FillerAudio, type Filling } from './voice-fillers.ts';
import type { VoiceCallbacks, VoiceModeStore, VoiceSnapshot } from './voice-mode.ts';
import { createVoiceTools, type ToolOutput } from './voice-tools.ts';

/** The stage's line on who hears the recruiter. */
export const ELEVENLABS_HEARS =
  "ElevenLabs hears you and speaks for kadmon; kadmon's answers are Claude's, and every question and answer is in this chat";

/** How long the connection may take to open before the mode ends with a note. */
export const ELEVENLABS_CONNECT_TIMEOUT_MS = 15_000;

/** About how long her voice takes per word (T436): a told line waits this long after she replies. */
export const ELEVENLABS_SPEECH_MS_PER_WORD = 400;

/**
 * T436a: how long after the recruiter last made a sound a told line waits for a reply that never
 * comes (a cough, a noise heard as words), before it counts her as done. Over the time she takes to
 * start a reply (about a second, live). T449 names it among the clocks a told answer still waits on
 * (this, `ELEVENLABS_SPEECH_MS_PER_WORD` and `ELEVENLABS_VOICE_GAP_MS`).
 */
export const ELEVENLABS_NO_REPLY_GRACE_MS = 5_000;

/** T436a: a voice-activity score above this is the recruiter speaking, before their words are final. */
export const ELEVENLABS_VAD_THRESHOLD = 0.5;

/** T436a: how long a voice-activity hit keeps the recruiter counted as speaking. */
export const ELEVENLABS_VOICE_GAP_MS = 600;

/** What the agent's script calls the recruiter when the brain does not know their name. */
export const UNNAMED_RECRUITER = 'the recruiter';

/**
 * A token minted ahead opens a conversation only this long after it was asked for. ElevenLabs'
 * tokens live fifteen minutes (read off one, 23 Sep 2026: `exp` 900 seconds after `nbf`); ten
 * leaves the call five to open in, and nothing here reads the token itself, which is theirs to
 * change.
 */
export const TOKEN_FRESH_MS = 10 * 60_000;

/**
 * The least time between two warm-ups: the panel asks on every open and after every conversation,
 * and once a minute is plenty — realtime.ts's `WARM_EVERY_MS`, on the same reasoning.
 */
export const ELEVENLABS_WARM_EVERY_MS = 60_000;

/**
 * How long before a token made ahead goes stale it is replaced, while the panel stays open
 * (24 Sep 2026): a panel read for a quarter of an hour would otherwise put the mint — a round
 * trip to ElevenLabs' servers in Iowa, 0.2 to 0.4 s measured from Natanel's machine — back on the
 * tap. A token a minute early costs nothing; each opens one conversation or none.
 */
export const TOKEN_REFRESH_EARLY_MS = 60_000;

/**
 * Where ElevenLabs' browser library opens a call over WebRTC (its `DEFAULT_LIVEKIT_WS_URL`, as
 * https), which the warm-up looks up ahead. elevenlabs.test.ts reads the library's own constant, so
 * an upgrade that moves it fails there rather than warming the wrong place in silence.
 */
export const ELEVENLABS_CALL_SERVER = 'https://livekit.rtc.elevenlabs.io';

/**
 * Where a call over their WebSocket opens (24 Sep 2026): the host of the signed URL the brain API
 * mints. Warmed ahead too — its name looked up and a connection made — so the socket's own
 * handshake finds that done.
 */
export const ELEVENLABS_SOCKET_SERVER = 'https://api.elevenlabs.io';

/** What the open call tells the store. */
export interface ElevenLabsHandlers {
  /** A client tool the agent called, with its arguments; what it answers goes back as JSON. */
  tool(name: string, args: Record<string, unknown>): Promise<string>;
  /** She started speaking, or went back to listening. */
  mode(mode: 'speaking' | 'listening'): void;
  /** What the recruiter said, as ElevenLabs heard it. */
  heard(text: string): void;
  /** T436a: the recruiter's voice is being heard, ahead of the words (voice activity). */
  voice(): void;
  /** What she said. */
  said(text: string): void;
  /** The call ended from the far side, or failed; the reason, when there is one. */
  closed(reason: string | null): void;
}

/** The open call: the microphone's switch, the hang-up, and how loud each side is. */
export interface ElevenLabsLink {
  mute(muted: boolean): void;
  close(): void;
  levels?(): OrbLevels | null;
  /**
   * A line from the brain for her to answer aloud (24 Sep 2026, voice-tools.ts `tell`): sent as a
   * typed message, which ElevenLabs answers and does not echo back as the recruiter's words.
   */
  tell?(line: string): void;
}

export type OpenElevenLabsLink = (
  session: ElevenLabsVoiceSession,
  handlers: ElevenLabsHandlers,
) => Promise<ElevenLabsLink>;

export interface ElevenLabsParts {
  /** A token for one conversation, minted for this recruiter (api/voice.ts). */
  session(): Promise<ElevenLabsVoiceSession>;
  open: OpenElevenLabsLink;
  /** Gets the library and the connection ready before Talk (`browserElevenLabsWarmUp`). */
  warm?(): void;
  /** T120: plays what she says while `ask_vera` runs (voice-fillers.ts); none, and she is quiet. */
  fillers?: FillerAudio;
  /** T449: a line for the harness's log (voice-tools.ts `trace`); the panel passes none. */
  trace?(what: string): void;
}

/** The tools' names, as the agent declares them — the only calls this file answers. */
export const ELEVENLABS_TOOL_NAMES = [
  'ask_vera',
  'look_up',
  'look_up_search',
  'look_up_waiting',
  // T595a removes these once the live agent is remade (voice-tools.ts `HOLDOVER_CARD_TOOLS`).
  'confirm_card',
  'dismiss_card',
] as const;

export function createElevenLabsVoice(
  parts: ElevenLabsParts,
  timeoutMs: number = ELEVENLABS_CONNECT_TIMEOUT_MS,
  now: () => number = Date.now,
): VoiceModeStore {
  let snap: VoiceSnapshot = {
    state: 'off',
    heard: '',
    said: '',
    muted: false,
    note: null,
    card: null,
    showing: false,
  };
  let callbacks: VoiceCallbacks | null = null;
  let link: ElevenLabsLink | null = null;
  // Bumped by start and end: a late connection or a late tool result finds it changed.
  let generation = 0;
  const listeners = new Set<() => void>();

  // The token minted ahead of Talk, unused, with when it was asked for; and one being minted. A
  // token opens one conversation, so a Talk takes it and nothing else ever sees it again.
  let ready: { session: ElevenLabsVoiceSession; at: number } | null = null;
  let minting: Promise<ElevenLabsVoiceSession> | null = null;
  const fresh = (kept: { at: number }) => now() - kept.at < TOKEN_FRESH_MS;
  // Between `prepare` and `unprepare` — the panel open, no conversation — the token made ahead
  // is replaced before it goes stale (`TOKEN_REFRESH_EARLY_MS`), and the connection warmed again.
  let keeping = false;
  let refresh: ReturnType<typeof setTimeout> | null = null;
  const stopKeeping = () => {
    keeping = false;
    if (refresh !== null) clearTimeout(refresh);
    refresh = null;
  };
  // The panel remounts on every chat it opens; one warm-up a minute is plenty.
  let warmedAt = -Infinity;
  const warm = () => {
    if (!parts.warm || now() - warmedAt < ELEVENLABS_WARM_EVERY_MS) return;
    warmedAt = now();
    parts.warm();
  };

  const mintAhead = () => {
    const at = now();
    const pending = parts.session();
    minting = pending;
    pending.then(
      (session) => {
        if (minting !== pending) return; // a Talk took it while it was being minted
        minting = null;
        ready = { session, at };
        if (keeping) {
          if (refresh !== null) clearTimeout(refresh);
          refresh = setTimeout(() => {
            refresh = null;
            if (!keeping || snap.state !== 'off' || minting) return;
            ready = null;
            warm();
            mintAhead();
          }, TOKEN_FRESH_MS - TOKEN_REFRESH_EARLY_MS);
        }
      },
      () => {
        // Ahead of time, a failed mint is nobody's error: Talk mints again, and says why if that
        // fails too.
        if (minting === pending) minting = null;
      },
    );
  };

  /** The token for the conversation opening now: the one minted ahead while it is good, else new. */
  const takeToken = (): Promise<ElevenLabsVoiceSession> => {
    const kept = ready;
    ready = null;
    if (kept && fresh(kept)) return Promise.resolve(kept.session);
    if (minting) {
      const pending = minting;
      minting = null;
      return pending;
    }
    return parts.session();
  };

  const set = (patch: Partial<VoiceSnapshot>) => {
    snap = { ...snap, ...patch };
    for (const listener of listeners) listener();
  };

  // T436: when they last spoke, and when she last answered and how long that takes to say. `mode`
  // reads `speaking` through much of a lookup (the tool's typing sound; measured, live2.log), so
  // the hold on an old answer asks these instead. T436a: "answered what was said last" is a count
  // (as OpenAI's `heardTurns`/`repliedTurns`), never two clock readings that can be equal; the
  // recruiter's voice counts from the first sound (`voice`), not the final transcript.
  let lastSoundAt = 0;
  let heardTurns = 0;
  let repliedTurns = 0;
  let lastSaid = { at: 0, spokenMs: 0 };
  // T442a: how many events of hers (`mode`, `said`) have come, and how many tool calls are running:
  // a tool's result that she never answers (nothing to say) must not leave the store `thinking`.
  let herEvents = 0;
  let toolsRunning = 0;
  let toolCalls = 0;
  // T442a: she may have nothing to say to a result or a told line (a reply that never comes); then
  // no event of hers follows and `thinking` would stay. After the grace with none of hers, no tool
  // running and still thinking, she is listening again.
  const restIfSilent = (mine: number) => {
    const eventsThen = herEvents;
    const callsThen = toolCalls;
    setTimeout(() => {
      if (
        mine === generation &&
        herEvents === eventsThen &&
        toolCalls === callsThen &&
        toolsRunning === 0 &&
        snap.state === 'thinking'
      ) {
        rest();
      }
    }, ELEVENLABS_NO_REPLY_GRACE_MS);
  };
  const herReplyDone = (): boolean => {
    const at = Date.now();
    if (at - lastSoundAt < ELEVENLABS_VOICE_GAP_MS) return false;
    // She has not replied to what was said last: a reply that is not coming (a noise heard as
    // words) is waited for only so long.
    if (repliedTurns < heardTurns) return at >= lastSoundAt + ELEVENLABS_NO_REPLY_GRACE_MS;
    // A reply that is itself a tool call keeps this wait on purpose: releasing sooner would talk
    // over the second lookup's fillers (T442a).
    return at >= lastSaid.at + lastSaid.spokenMs;
  };
  // The tools' `tell`: a line from the brain she answers aloud. It owes her a reply, so it is a turn
  // for `herReplyDone` as well (`heardTurns`, `lastSoundAt`), `thinking` until she speaks, and any
  // lookup's filler stops; if she has nothing to say the state goes back after the grace (T449).
  const tellHer = (line: string) => {
    if (!link?.tell) return;
    hush();
    lastSoundAt = Date.now();
    heardTurns += 1;
    set({ state: 'thinking' });
    link.tell(line);
    restIfSilent(generation);
  };
  const tools = createVoiceTools({
    callbacks: () => callbacks,
    herReplyDone,
    // T449: measured, 24 Oct 2026 (handoffs/sessions/T449.md § Evidence): a result that arrives
    // after the recruiter spoke is taken (`agent_tool_response` success) and never voiced, 5 of 5
    // replays and a socket test; the same result with nothing said meanwhile is voiced.
    dropsLateResults: true,
    ...(parts.trace ? { trace: parts.trace } : {}),
    snapshot: () => snap,
    set: (patch) => set(patch),
    generation: () => generation,
    tell: tellHer,
  });

  // T120: the fillers of every lookup running, a closed conversation silences them; T436: so does
  // the recruiter speaking again, since a clip over her next reply is two voices at once.
  const filling = new Set<Filling>();
  const hush = () => {
    for (const each of filling) each.cancel();
    filling.clear();
  };
  // A transcript inside the newest lookup's window is the rest of that question, but it is also the
  // recruiter moving on from any lookup begun before it: those fillers stop, the newest plays on.
  const hushOlder = () => {
    const newest = [...filling].at(-1);
    for (const each of [...filling]) {
      if (each === newest) continue;
      each.cancel();
      filling.delete(each);
    }
  };

  const end = (note: string | null) => {
    generation += 1;
    tools.reset();
    hush();
    lastSoundAt = 0;
    heardTurns = 0;
    repliedTurns = 0;
    lastSaid = { at: 0, spokenMs: 0 };
    // No test can see these counters reset: her first reply after a restart sets `repliedTurns`
    // from `heardTurns` again (`said`), and `herReplyDone` reads only the pair (T442a).
    link?.close();
    link = null;
    set({ state: 'off', heard: '', said: '', note, card: null, showing: false });
  };

  const rest = () => set({ state: snap.muted ? 'muted' : 'listening' });

  const handlers = (mine: number): ElevenLabsHandlers => ({
    async tool(name, args) {
      if (mine !== generation) return JSON.stringify({ error: 'The conversation has ended.' });
      set({ state: 'thinking' });
      toolsRunning += 1;
      toolCalls += 1;
      // T120: a lookup is filled the way a person fills one, and a card's quick yes or no is not;
      // since T120a by the lookup's own stages, which the ask hands the fillers as they happen
      // ("Okay, nearly there" only while the answer is being written). Her microphone is shut only
      // while a clip plays, and handed back as the recruiter left it.
      const lookupFilling =
        name === 'ask_vera' && parts.fillers
          ? startFillers(parts.fillers, { mute: (on) => link?.mute(on || snap.muted) })
          : null;
      if (lookupFilling) filling.add(lookupFilling);
      let output: ToolOutput | null;
      try {
        output = await tools.run(name, args, lookupFilling ?? undefined);
      } finally {
        toolsRunning -= 1;
      }
      // T436a: an answer comes back after the recruiter spoke again, so the state may read
      // listening; a correction waiting on that state must not count the gap before her reply as
      // quiet (realtime.ts `runTool` does the same). T449: not `later`, which she has no reply to.
      if (
        output !== null &&
        !('later' in output) &&
        mine === generation &&
        (snap.state === 'listening' || snap.state === 'muted')
      ) {
        set({ state: 'thinking' });
      }
      if (lookupFilling) {
        await lookupFilling.finish();
        filling.delete(lookupFilling);
      }
      if (output !== null && mine === generation) restIfSilent(mine);
      return JSON.stringify(output ?? { error: 'The conversation has ended.' });
    },
    mode(mode) {
      if (mine !== generation || snap.state === 'off') return;
      herEvents += 1;
      if (mode === 'speaking') set({ state: 'speaking' });
      else rest();
    },
    heard(text) {
      if (mine !== generation || snap.state === 'off') return;
      // The recruiter spoke, and what they said: what makes a second yes theirs (voice-tools.ts).
      const counted = tools.heard(text);
      lastSoundAt = Date.now();
      heardTurns += 1;
      // T442a: the rest of the question a lookup began on is not the recruiter moving on: the
      // lookup's fillers play on.
      if (counted) hush();
      else hushOlder();
      set({ heard: text, said: '' });
    },
    voice() {
      if (mine !== generation || snap.state === 'off') return;
      lastSoundAt = Date.now();
    },
    said(text) {
      if (mine !== generation || snap.state === 'off') return;
      herEvents += 1;
      tools.spoke();
      // The expressive model may carry a delivery cue in brackets ("[warmly]"): heard, never shown,
      // and never counted as a spoken word.
      const spoken = text.replace(/\[[^\]]*\]\s*/g, '').trim();
      // Her words arrive as the reply is written, the audio some seconds behind: estimate how long it takes.
      repliedTurns = heardTurns;
      lastSaid = {
        at: Date.now(),
        spokenMs: spoken.split(/\s+/).filter(Boolean).length * ELEVENLABS_SPEECH_MS_PER_WORD,
      };
      set({ said: spoken });
    },
    closed(reason) {
      if (mine !== generation || snap.state === 'off') return;
      end(reason);
    },
  });

  const start = async () => {
    if (snap.state !== 'off') return;
    stopKeeping();
    generation += 1;
    const mine = generation;
    set({ state: 'connecting', note: null, card: null, showing: false, heard: '', said: '' });
    const timer = setTimeout(() => {
      if (mine === generation && snap.state === 'connecting') {
        end('The voice connection did not open. Try Talk again, or type the question.');
      }
    }, timeoutMs);
    try {
      const session = await takeToken();
      if (mine !== generation) return;
      const opened = await parts.open(session, handlers(mine));
      if (mine !== generation) {
        opened.close(); // the mode gave up waiting, or was ended, while it opened
        return;
      }
      link = opened;
      if (snap.muted) link.mute(true);
      rest();
    } catch (error) {
      if (mine === generation) {
        end(
          error instanceof Error && error.message
            ? `The voice connection did not open: ${error.message}`
            : 'The voice connection did not open. Try Talk again, or type the question.',
        );
      }
    } finally {
      clearTimeout(timer);
    }
  };

  return {
    snapshot: () => snap,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    bind(next) {
      callbacks = next;
    },
    start: () => void start(),
    // While the panel is open and no conversation is: the token minted ahead, and the library and
    // the connection warmed (the head of this file). The panel asks again after each conversation.
    prepare() {
      if (snap.state !== 'off') return;
      keeping = true;
      warm();
      if (ready && fresh(ready)) return;
      if (!minting) mintAhead();
    },
    unprepare: stopKeeping,
    end: () => end(null),
    toggleMute() {
      const muted = !snap.muted;
      link?.mute(muted);
      set({
        muted,
        ...(snap.state === 'listening' || snap.state === 'muted'
          ? { state: muted ? 'muted' : 'listening' }
          : {}),
      });
    },
    // ElevenLabs stops her itself when the recruiter talks over her; a tap has nothing to add.
    interrupt() {},
    levels: () => (link?.levels ? link.levels() : null),
  };
}

/**
 * T449 (24 Oct 2026): the one line of the harness's log for a raw event of the library's, or null
 * for the many it does not care about. What it shows is whether ElevenLabs takes a tool's result
 * that comes after the recruiter spoke again, and whether it speaks it. Field names are the
 * protocol's as `@elevenlabs/client` 1.25.0 types them; a missing one prints as `?`.
 */
export function rawEventMark(direction: 'in' | 'out', event: unknown): string | null {
  if (typeof event !== 'object' || event === null) return null;
  const e = event as Record<string, unknown>;
  const inner = (key: string): Record<string, unknown> =>
    typeof e[key] === 'object' && e[key] !== null ? (e[key] as Record<string, unknown>) : {};
  const text = (value: unknown): string => String(value ?? '?').slice(0, 60);
  if (direction === 'out') {
    return e.type === 'client_tool_result'
      ? `raw out client_tool_result ${text(e.tool_call_id)} error=${text(e.is_error)}`
      : null;
  }
  switch (e.type) {
    case 'client_tool_call': {
      const call = inner('client_tool_call');
      return `raw in client_tool_call ${text(call.tool_name)} ${text(call.tool_call_id)}`;
    }
    case 'agent_tool_response': {
      const done = inner('agent_tool_response');
      return `raw in agent_tool_response ${text(done.tool_name)} ${text(done.tool_call_id)} error=${text(done.is_error)}`;
    }
    case 'interruption':
      return 'raw in interruption';
    case 'user_transcript':
      return `raw in user_transcript: ${text(inner('user_transcription_event').user_transcript)}`;
    case 'agent_response':
      return `raw in agent_response: ${text(inner('agent_response_event').agent_response)}`;
    default:
      return null;
  }
}

/**
 * The adapter onto ElevenLabs' browser library, or null where the browser cannot hold a call.
 * The library is loaded when Talk opens, not with the brain: most of a day's pages never talk.
 */
export function browserElevenLabsLink(
  scope: object,
  trace?: (what: string) => void,
): OpenElevenLabsLink | null {
  const { RTCPeerConnection: PeerConnection, navigator: nav } = scope as {
    RTCPeerConnection?: typeof RTCPeerConnection;
    navigator?: Navigator;
  };
  if (!PeerConnection || !nav?.mediaDevices?.getUserMedia) return null;
  return async (session, on) => {
    const { Conversation } = await import('@elevenlabs/client');
    const tool =
      (name: string) =>
      (args: unknown): Promise<string> =>
        on.tool(name, (args ?? {}) as Record<string, unknown>);
    // Their WebSocket on a signed URL (the head of this file), or WebRTC on a token.
    const where =
      session.transport === 'websocket'
        ? { signedUrl: session.signedUrl, connectionType: 'websocket' as const }
        : { conversationToken: session.conversationToken, connectionType: 'webrtc' as const };
    const conversation = await Conversation.startSession({
      ...where,
      dynamicVariables: { recruiter_name: session.recruiterName ?? UNNAMED_RECRUITER },
      clientTools: Object.fromEntries(ELEVENLABS_TOOL_NAMES.map((name) => [name, tool(name)])),
      onModeChange: ({ mode }) => on.mode(mode),
      onMessage: ({ message, source }) =>
        source === 'user' ? on.heard(message) : on.said(message),
      // T436a: the recruiter's voice, ahead of their final words.
      onVadScore: ({ vadScore }) => {
        if (vadScore > ELEVENLABS_VAD_THRESHOLD) on.voice();
      },
      onDisconnect: (details) =>
        on.closed(
          details.reason === 'error' ? `The voice connection dropped: ${details.message}` : null,
        ),
      // An error is not an ending: the library reports ones the call survives too. A call that
      // does not survive ends through `onDisconnect`, with the reason, above.
      onError: () => {},
      // T449: the library's raw events, for the harness's log only.
      ...(trace
        ? {
            onIncomingEvent: (event: unknown) => {
              const what = rawEventMark('in', event);
              if (what) trace(what);
            },
            onOutgoingEvent: (event: unknown) => {
              const what = rawEventMark('out', event);
              if (what) trace(what);
            },
          }
        : {}),
    });
    return {
      mute: (muted) => conversation.setMicMuted(muted),
      close: () => void conversation.endSession(),
      levels: () => ({
        input: conversation.getInputVolume(),
        output: conversation.getOutputVolume(),
      }),
      tell: (line) => conversation.sendUserMessage(line),
    };
  };
}

/**
 * Gets a Talk on ElevenLabs ready (23 Sep 2026): their browser library loaded now rather than on
 * the tap — the call's own `import` then finds it — and a HEAD with nothing on it to where the call
 * connects, so the call finds DNS and TLS done, as realtime.ts's `browserWarmUp` does for OpenAI.
 * Both of their servers: the WebSocket's, which a call opens on since 24 Sep 2026, and WebRTC's,
 * one word away (services/bff/src/elevenlabs.ts `ELEVENLABS_TRANSPORT`). It tells ElevenLabs no
 * more than the call's own connection will, and runs only where the brain's voice is on ElevenLabs
 * and the panel is open.
 */
export function browserElevenLabsWarmUp(
  scope: object,
  load: () => Promise<unknown> = () => import('@elevenlabs/client'),
): () => void {
  const { fetch: fetchImpl } = scope as { fetch?: typeof fetch };
  return () => {
    void load().catch(() => {});
    for (const server of [ELEVENLABS_SOCKET_SERVER, ELEVENLABS_CALL_SERVER]) {
      void fetchImpl
        ?.call(scope, server, { method: 'HEAD', mode: 'no-cors', credentials: 'omit' })
        .catch(() => {});
    }
  };
}
